/**
 * PATH: packages/email-hub/src/feature/components/SnoozeMenuButton.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-SNOOZEMENUBUTTON-001
 * PURPOSE: Self-contained snooze button + preset dropdown for the
 *          EmailReader toolbar (and any other toolbar surface). Presets
 *          come from the shared resurfaceTimes module; the click fires
 *          the caller-supplied onSnooze(iso) so the data layer stays with
 *          the host (reader uses bulkAction 'snooze').
 * WHY: Resurface train (composer audit 2026-07-06) — snooze needs to be
 *      reachable from the open reader, not only the row right-click.
 *      The reader's toolbar actions are plain render callbacks, so the
 *      dropdown state has to live in its own component.
 * RELATED:
 *   - lib/resurfaceTimes.ts: preset arithmetic (one source, three surfaces)
 *   - components/EmailContextMenu.tsx: the row right-click sibling
 */
import { useState } from 'react'
import { Clock } from 'lucide-react'

import { formatResurfaceTime, snoozePresets } from '../lib/resurfaceTimes'

export function SnoozeMenuButton({
  onSnooze,
}: {
  /** Called with the ISO wake time; host owns the API call + navigation. */
  onSnooze: (snoozedUntilIso: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-md hover:bg-accent"
        aria-label="Snooze email"
        aria-expanded={open}
        title="Snooze — hide until a later time"
      >
        <Clock className="h-4 w-4 text-muted-foreground" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-[71] w-56 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg py-1">
            <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Snooze until
            </div>
            {snoozePresets().map((p) => (
              <button
                key={p.key}
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center justify-between gap-2"
                onClick={() => {
                  setOpen(false)
                  onSnooze(p.at.toISOString())
                }}
              >
                <span>{p.label}</span>
                <span className="text-[10px] text-muted-foreground">
                  {formatResurfaceTime(p.at)}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  )
}
